import React, { useState, useEffect, useRef } from 'react'
import {
  Card, Table, Tag, Typography, theme,
  Space, Button, Popconfirm,
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, DownOutlined, UpOutlined } from '@ant-design/icons'
import {
  useCostItemsForWorkOrder,
  useCreateCostItem,
  useUpdateCostItem,
  useDeleteCostItem,
} from '@/hooks/useCostItems'
import { usePermission } from '@/hooks/usePermission'
import CostoModal from './CostoModal'
import type { WorkOrderCostItem, CostItemCreate, CostItemUpdate } from '@/types/costItems'

const { Text } = Typography

interface StatItemProps {
  label: string
  value: string
  color?: string
}

const StatItem: React.FC<StatItemProps> = ({ label, value, color }) => {
  const { token } = theme.useToken()
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Text type="secondary" style={{ fontSize: 11 }}>{label}</Text>
      <Text strong style={{ fontSize: 13, color: color ?? token.colorText }}>{value}</Text>
    </div>
  )
}

interface CostiPanelProps {
  workOrderId: number
  collapsible?: boolean
}

const formatEuro = (val: string | number | null | undefined): string => {
  if (val == null) return '—'
  return `${Number(val).toFixed(2)} €`
}

const CostiPanel: React.FC<CostiPanelProps> = ({ workOrderId, collapsible = true }) => {
  const { token } = theme.useToken()
  const { data, isLoading } = useCostItemsForWorkOrder(workOrderId)

  const canCreate = usePermission('costs.create')
  const canEdit   = usePermission('costs.edit')
  const canDelete = usePermission('costs.delete')

  const createCostItem = useCreateCostItem(workOrderId)
  const updateCostItem = useUpdateCostItem(workOrderId)
  const deleteCostItem = useDeleteCostItem(workOrderId)

  const [modalOpen,    setModalOpen]    = useState(false)
  const [editingItem,  setEditingItem]  = useState<WorkOrderCostItem | null>(null)

  const items  = data?.items ?? []
  const totali = data?.totali

  // ── Collasso ──────────────────────────────────────────────────────────────────
  const [aperto, setAperto] = useState(false)
  const autoApertoRef = useRef(false)

  useEffect(() => {
    if (items.length > 0 && !autoApertoRef.current) {
      autoApertoRef.current = true
      setAperto(true)
    }
  }, [items.length])

  const corpoVisibile = collapsible === false || aperto

  const summaryTesto = items.length > 0 && totali
    ? formatEuro(totali.totale_documento)
    : 'scheda non ancora quotata'

  const handleConfirm = async (payload: CostItemCreate | CostItemUpdate) => {
    try {
      if (editingItem) {
        await updateCostItem.mutateAsync({ id: editingItem.id, data: payload as CostItemUpdate })
      } else {
        await createCostItem.mutateAsync(payload as CostItemCreate)
      }
      setModalOpen(false)
      setEditingItem(null)
    } catch {
      // errore già mostrato dall'hook
    }
  }

  const imponibileAss = totali ? Number(totali.imponibile_assicurazione) : 0

  const columns = [
    {
      title: 'Descrizione',
      dataIndex: 'descrizione',
      render: (v: string, record: WorkOrderCostItem) => (
        <div>
          <Text style={{ fontSize: 12 }}>{v}</Text>
          {!record.attiva && record.motivo_esclusione && (
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                Esclusa: {record.motivo_esclusione}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Q.tà',
      dataIndex: 'quantita',
      width: 62,
      render: (v: string | number) => (
        <Text style={{ fontSize: 12 }}>{Number(v).toFixed(2)}</Text>
      ),
    },
    {
      title: 'U.M.',
      dataIndex: 'unita_misura',
      width: 55,
      render: (v: string | null) => (
        <Text type="secondary" style={{ fontSize: 12 }}>{v ?? '—'}</Text>
      ),
    },
    {
      title: 'Prezzo',
      dataIndex: 'prezzo_unitario',
      width: 88,
      render: (v: string | number) => (
        <Text style={{ fontSize: 12 }}>{formatEuro(v)}</Text>
      ),
    },
    {
      title: 'Sc.%',
      dataIndex: 'sconto_percentuale',
      width: 52,
      render: (v: string | number) => (
        <Text style={{ fontSize: 12 }}>
          {Number(v) > 0 ? `${Number(v).toFixed(0)}%` : '—'}
        </Text>
      ),
    },
    {
      title: 'IVA',
      dataIndex: 'aliquota_iva',
      width: 50,
      render: (v: string | number) => (
        <Text style={{ fontSize: 12 }}>{Number(v).toFixed(0)}%</Text>
      ),
    },
    {
      title: 'Imponibile',
      dataIndex: 'imponibile_riga',
      width: 90,
      render: (v: string | number) => (
        <Text style={{ fontSize: 12 }}>{formatEuro(v)}</Text>
      ),
    },
    {
      title: 'Totale',
      dataIndex: 'totale_riga',
      width: 90,
      render: (v: string | number) => (
        <Text strong style={{ fontSize: 12 }}>{formatEuro(v)}</Text>
      ),
    },
    {
      title: 'Debitore',
      dataIndex: 'debitore',
      width: 105,
      render: (v: string) => (
        <Tag
          color={v === 'assicurazione' ? 'orange' : 'blue'}
          style={{ fontSize: 11 }}
        >
          {v === 'assicurazione' ? 'Assicuraz.' : 'Cliente'}
        </Tag>
      ),
    },
    {
      title: '',
      width: 72,
      render: (_: unknown, record: WorkOrderCostItem) => (
        <Space size={4}>
          {canEdit && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingItem(record)
                setModalOpen(true)
              }}
            />
          )}
          {canDelete && (
            <Popconfirm
              title="Eliminare questa riga di costo?"
              okText="Sì"
              cancelText="No"
              onConfirm={() => deleteCostItem.mutate(record.id)}
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={deleteCostItem.isPending}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  const cardExtra = (canCreate || collapsible) ? (
    <Space size={8}>
      {canCreate && (
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingItem(null)
            setModalOpen(true)
          }}
        >
          Aggiungi riga
        </Button>
      )}
      {collapsible && (
        <Button
          type="text"
          size="small"
          icon={aperto ? <UpOutlined /> : <DownOutlined />}
          onClick={() => setAperto(o => !o)}
        />
      )}
    </Space>
  ) : null

  return (
    <>
      <Card
        size="small"
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Text strong style={{ fontSize: 14 }}>Costi</Text>
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>{summaryTesto}</Text>
          </span>
        }
        loading={isLoading}
        extra={cardExtra}
        styles={{ body: { display: corpoVisibile ? undefined : 'none' } }}
      >
        {totali && items.length > 0 && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            padding: '8px 12px',
            background: token.colorFillAlter,
            borderRadius: token.borderRadiusSM,
            marginBottom: 12,
          }}>
            <StatItem label="Imponibile"       value={formatEuro(totali.totale_imponibile)} />
            <StatItem label="IVA"              value={formatEuro(totali.totale_iva)} />
            <StatItem
              label="Totale documento"
              value={formatEuro(totali.totale_documento)}
              color={token.colorPrimary}
            />
            {imponibileAss > 0 && (
              <>
                <div style={{ width: 1, background: token.colorBorder, alignSelf: 'stretch' }} />
                <StatItem label="Cliente"       value={formatEuro(totali.imponibile_cliente)} />
                <StatItem label="Assicurazione" value={formatEuro(totali.imponibile_assicurazione)} />
              </>
            )}
            {totali.righe_escluse > 0 && (
              <StatItem
                label="Righe escluse"
                value={String(totali.righe_escluse)}
                color={token.colorTextSecondary}
              />
            )}
          </div>
        )}

        {items.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 13 }}>Nessun costo registrato.</Text>
        ) : (
          <Table<WorkOrderCostItem>
            dataSource={items}
            columns={columns}
            rowKey="id"
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
            onRow={(record) => ({
              style: record.attiva
                ? {}
                : { background: token.colorFillContent, opacity: 0.65 },
            })}
          />
        )}
      </Card>

      <CostoModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setEditingItem(null)
        }}
        onConfirm={handleConfirm}
        loading={createCostItem.isPending || updateCostItem.isPending}
        costItem={editingItem ?? undefined}
      />
    </>
  )
}

export default CostiPanel
